import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheoryProgress } from '../../hooks/useTheoryProgress';
import { useVideoProgress } from '../../hooks/useVideoProgress';

const StackQueue: React.FC = () => {
  const navigate = useNavigate();
  useTheoryProgress('stackQueue');
  const videoRef = useVideoProgress('stackQueue');

  const handleBackClick = () => {
    navigate('/topics');
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Minimal grid background */}
      <div className="fixed inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:64px_64px] [background-position-x:center]"></div>
      
      <div className="relative z-10 max-w-5xl mx-auto px-4 py-8">
        {/* Header with back button */}
        <div className="flex items-center justify-between mb-8">
          <motion.button
            onClick={handleBackClick}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            whileHover={{ x: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="text-sm font-medium">Back to Topics</span>
          </motion.button>
        </div>

        {/* Title and description */}
        <motion.div 
          className="mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-normal text-white mb-2">Stacks & Queues</h1>
          <p className="text-white/70 text-lg">Learn about stack and queue data structures.</p>
        </motion.div>

        <div className="space-y-12">
          {/* Introduction */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-4">What Are Stacks & Queues?</h2>
            <div className="text-white/70 space-y-4">
              <p>
                Stacks and queues are linear data structures with strict rules for adding and removing elements. 
                Stacks operate on a Last-In-First-Out (LIFO) basis, while queues follow First-In-First-Out (FIFO).
              </p>
              <p>
                Picture a stack as a pile of plates—you add and remove from the top. 
                A queue is like a line at a store—first in line is first served.
              </p>
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6 mt-6">
                <h3 className="text-xl font-semibold text-white mb-3">Core Characteristics</h3>
                <ul className="list-disc list-inside text-white/70 space-y-2">
                  <li><strong>LIFO (Stack):</strong> Last element in, first out</li>
                  <li><strong>FIFO (Queue):</strong> First element in, first out</li>
                  <li><strong>Restricted Access:</strong> Limited insertion/removal points</li>
                  <li><strong>Dynamic:</strong> Can grow/shrink as needed</li>
                </ul>
              </div>
            </div>
          </motion.section>

          {/* Stack Implementation */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-6">Stack Implementation</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white">Array-Based Stack</h3>
                <div className="text-2xl">📚</div>
              </div>
              <p className="text-white/70 mb-4">
                A simple stack implementation using an array with LIFO operations.
              </p>
              <div className="bg-white/5 p-4 rounded-lg">
                <pre className="text-sm text-white/80 font-mono">
                  <code>{`class Stack<T> {
  private items: T[];

  constructor() {
    this.items = [];
  }
  
  push(element: T): void {
    this.items.push(element); // O(1) amortized
  }
  
  pop(): T | null {
    if (this.isEmpty()) return null;
    return this.items.pop()!; // O(1)
  }
  
  peek(): T | null {
    if (this.isEmpty()) return null;
    return this.items[this.items.length - 1]; // O(1)
  }
  
  isEmpty(): boolean {
    return this.items.length === 0; // O(1)
  }
  
  size(): number {
    return this.items.length; // O(1)
  }
}

const stack = new Stack<number>();
stack.push(1);
stack.push(2);
console.log(stack.pop()); // 2`}</code>
                </pre>
              </div>
            </div>
          </motion.section>

          {/* Queue Implementation */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-6">Queue Implementation</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white">Array-Based Queue</h3>
                <div className="text-2xl">🚶‍♂️</div>
              </div>
              <p className="text-white/70 mb-4">
                A basic queue implementation using an object with FIFO operations.
              </p>
              <div className="bg-white/5 p-4 rounded-lg">
                <pre className="text-sm text-white/80 font-mono">
                  <code>{`class Queue<T> {
  private items: { [key: number]: T };
  private frontIndex: number;
  private backIndex: number;

  constructor() {
    this.items = {};
    this.frontIndex = 0;
    this.backIndex = 0;
  }
  
  enqueue(element: T): void {
    this.items[this.backIndex] = element; // O(1)
    this.backIndex++;
  }
  
  dequeue(): T | null {
    if (this.isEmpty()) return null;
    const item = this.items[this.frontIndex];
    delete this.items[this.frontIndex]; // O(1)
    this.frontIndex++;
    return item;
  }
  
  peek(): T | null {
    if (this.isEmpty()) return null;
    return this.items[this.frontIndex]; // O(1)
  }
  
  isEmpty(): boolean {
    return this.frontIndex === this.backIndex; // O(1)
  }
  
  size(): number {
    return this.backIndex - this.frontIndex; // O(1)
  }
}

const queue = new Queue<number>();
queue.enqueue(1);
queue.enqueue(2);
console.log(queue.dequeue()); // 1`}</code>
                </pre>
              </div>
            </div>
          </motion.section>

          {/* Advanced Implementations */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-6">Advanced Implementations</h2>
            <div className="space-y-6">
              {/* Circular Queue */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Circular Queue</h3>
                  <div className="text-2xl">🔄</div>
                </div>
                <p className="text-white/70 mb-4">
                  A fixed-size queue that reuses space by wrapping around.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`class CircularQueue<T> {
  private items: (T | null)[];
  private front: number;
  private rear: number;
  private capacity: number;

  constructor(size: number) {
    this.items = new Array(size).fill(null);
    this.front = -1;
    this.rear = -1;
    this.capacity = size;
  }

  enqueue(element: T): boolean {
    if (this.isFull()) return false;
    if (this.isEmpty()) this.front = 0;
    this.rear = (this.rear + 1) % this.capacity;
    this.items[this.rear] = element;
    return true;
  }

  dequeue(): T | null {
    if (this.isEmpty()) return null;
    const item = this.items[this.front]!;
    this.items[this.front] = null;
    if (this.front === this.rear) {
      this.front = -1;
      this.rear = -1;
    } else {
      this.front = (this.front + 1) % this.capacity;
    }
    return item;
  }

  isFull(): boolean {
    return ((this.rear + 1) % this.capacity) === this.front;
  }

  isEmpty(): boolean {
    return this.front === -1;
  }
}

const cq = new CircularQueue<number>(3);
cq.enqueue(1); cq.enqueue(2);
console.log(cq.dequeue()); // 1`}</code>
                  </pre>
                </div>
              </div>

              {/* Deque */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Deque (Double-Ended Queue)</h3>
                  <div className="text-2xl">↔️</div>
                </div>
                <p className="text-white/70 mb-4">
                  A queue allowing insertion and removal from both ends.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`class Deque<T> {
  private items: T[];

  constructor() {
    this.items = [];
  }

  addFront(element: T): void {
    this.items.unshift(element); // O(n)
  }

  addRear(element: T): void {
    this.items.push(element); // O(1) amortized
  }

  removeFront(): T | null {
    if (this.isEmpty()) return null;
    return this.items.shift()!; // O(n)
  }

  removeRear(): T | null {
    if (this.isEmpty()) return null;
    return this.items.pop()!; // O(1)
  }

  isEmpty(): boolean {
    return this.items.length === 0;
  }
}

const deque = new Deque<number>();
deque.addRear(1);
deque.addFront(2);
console.log(deque.removeFront()); // 2`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Applications */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-6">Real-World Applications</h2>
            <div className="space-y-6">
              {/* Stack Applications */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Stack Applications</h3>
                  <div className="text-2xl">🛠️</div>
                </div>
                <p className="text-white/70 mb-4">
                  Practical uses leveraging LIFO behavior.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`// Postfix expression evaluation
function evaluatePostfix(expr: string[]): number {
  const stack = new Stack<number>();
  
  for (const token of expr) {
    if (!isNaN(Number(token))) {
      stack.push(Number(token));
    } else {
      const b = stack.pop()!;
      const a = stack.pop()!;
      switch (token) {
        case '+': stack.push(a + b); break;
        case '-': stack.push(a - b); break;
        case '*': stack.push(a * b); break;
        case '/': stack.push(a / b); break;
      }
    }
  }
  
  return stack.pop()!;
}

console.log(evaluatePostfix(['2', '3', '+', '4', '*'])); // 20`}</code>
                  </pre>
                </div>
              </div>

              {/* Queue Applications */}
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">Queue Applications</h3>
                  <div className="text-2xl">🏃</div>
                </div>
                <p className="text-white/70 mb-4">
                  Practical uses leveraging FIFO behavior.
                </p>
                <div className="bg-white/5 p-4 rounded-lg">
                  <pre className="text-sm text-white/80 font-mono">
                    <code>{`// BFS using queue
function bfs(graph: { [key: string]: string[] }, start: string): Set<string> {
  const queue = new Queue<string>();
  const visited = new Set<string>();
  
  queue.enqueue(start);
  visited.add(start);
  
  while (!queue.isEmpty()) {
    const vertex = queue.dequeue()!;
    for (const neighbor of graph[vertex]) {
      if (!visited.has(neighbor)) {
        visited.add(neighbor);
        queue.enqueue(neighbor);
      }
    }
  }
  
  return visited;
}

const graph = { 'A': ['B', 'C'], 'B': ['A', 'D'], 'C': ['A'], 'D': ['B'] };
console.log(bfs(graph, 'A')); // Set {'A', 'B', 'C', 'D'}`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Time Complexity */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Time Complexity</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <p className="text-white/70 mb-4">
                Core operation costs for array-based implementations:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-white/70">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="py-3 px-4 text-left">Operation</th>
                      <th className="py-3 px-4 text-left">Stack</th>
                      <th className="py-3 px-4 text-left">Queue</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-white/5">
                      <td className="py-3 px-4">Push/Enqueue</td>
                      <td className="py-3 px-4">O(1) amortized</td>
                      <td className="py-3 px-4">O(1)</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-3 px-4">Pop/Dequeue</td>
                      <td className="py-3 px-4">O(1)</td>
                      <td className="py-3 px-4">O(1)</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-3 px-4">Peek</td>
                      <td className="py-3 px-4">O(1)</td>
                      <td className="py-3 px-4">O(1)</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Search</td>
                      <td className="py-3 px-4">O(n)</td>
                      <td className="py-3 px-4">O(n)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </motion.section>

          {/* Best Practices */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Best Practices</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <p className="text-white/70 mb-4">
                Guidelines for effective use:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Do's</h3>
                  <ul className="list-disc list-inside text-white/70 space-y-2">
                    <li>Validate emptiness before operations</li>
                    <li>Match structure to use case</li>
                    <li>Track size for capacity control</li>
                    <li>Use generics for type safety</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Don'ts</h3>
                  <ul className="list-disc list-inside text-white/70 space-y-2">
                    <li>Don't bypass LIFO/FIFO rules</li>
                    <li>Don't ignore overflow in fixed-size</li>
                    <li>Don't use for random access</li>
                    <li>Don't neglect cleanup</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Video Tutorial */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white/5 rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Video Tutorial</h2>
            <div className="aspect-w-16 aspect-h-9 bg-black/50 rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                >
                  <source src="https://files.catbox.moe/sdv5o0.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
            </div>
          </motion.section>

          {/* Why It Matters */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Why Stacks & Queues Matter</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <ul className="space-y-4 text-white/70">
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Simplicity:</strong> Clear rules for data flow.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Efficiency:</strong> O(1) core operations.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Versatility:</strong> Basis for algorithms and systems.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Real-World:</strong> Used in scheduling, parsing, and more.</span>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Next Steps */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.8 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Next Steps</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <p className="text-white/70 mb-4">
                With stack and queue knowledge, you can:
              </p>
              <ul className="list-disc list-inside text-white/70 space-y-2">
                <li>Implement LIFO/FIFO-based solutions</li>
                <li>Optimize for specific use cases</li>
                <li>Explore advanced variants</li>
                <li>Practice real-world applications</li>
              </ul>
              <div className="mt-6 flex justify-center">
                <motion.button
                  onClick={() => window.location.href = 'http://localhost:3000/practice/stack-queue'}
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors flex items-center gap-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span>Start Practice</span>
                </motion.button>
              </div>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default StackQueue;